<template>
    <ResponsiveDialog
        :value="value"
        @input="$emit('input', $event)"
        :max-width="420"
        :full-height="false"
        :header-title="isChallenge ? $t('journey.dungeonDetail.titleChallenge') : $t('journey.dungeonDetail.titleNormal')"
        header-class="skin-window-header--dungeon"
        card-class="skin-card"
    >
        <div class="dungeon-dialog-bg skin-window--dungeon">
            <div class="dungeon-dialog-banner" :style="dungeonBgStyle">
                <v-img v-if="bossImg" :src="bossImg" contain class="dungeon-dialog-boss-img"/>
                <span v-else class="dungeon-dialog-mystery">?</span>
                <div v-if="bossName" class="dungeon-dialog-boss-name">{{ bossName }}</div>
            </div>
            <div class="dungeon-dialog-content">
                <div class="dungeon-dialog-row">
                    <!-- Bez explicitního color propu - dědí barvu od .dungeon-dialog-row (theme-adaptivní). -->
                    <v-icon size="31" class="mr-1">mdi-map-marker-path</v-icon>
                    {{ $t('journey.dungeonDetail.lengthPrefix') }} {{ dungeon.steps }}
                </div>
                <div class="dungeon-dialog-row">
                    <v-icon size="31" class="mr-1">mdi-treasure-chest</v-icon>
                    {{ $t('journey.dungeonDetail.rewardPrefix') }} <v-img v-if="rewardStoneImg" :src="rewardStoneImg" contain class="dungeon-dialog-reward-stone ml-1"/>
                </div>
                <div v-if="obstacleHints.length > 0" class="dungeon-dialog-obstacles">
                    <div class="dungeon-dialog-obstacles-label">{{ $t('journey.dungeonDetail.obstaclesLabel') }}</div>
                    <div class="dungeon-dialog-obstacles-row">
                        <v-tooltip v-for="hint in obstacleHints" :key="hint.id" bottom open-delay="350">
                            <template v-slot:activator="{ on, attrs }">
                                <v-img
                                    v-bind="attrs"
                                    v-on="on"
                                    :src="require('@/assets/pic/obstacles/' + hint.img)"
                                    max-width="40"
                                    max-height="40"
                                    class="dungeon-dialog-obstacle-icon"
                                />
                            </template>
                            <span>{{ hint.name }}</span>
                        </v-tooltip>
                    </div>
                </div>
                <div
                    class="dungeon-dialog-action-pill"
                    :class="{'dungeon-dialog-action-pill--loading': isLoading, 'dungeon-dialog-action-pill--disabled': !canGo}"
                    @click="canGo && go()"
                >
                    <v-progress-circular v-if="isLoading" indeterminate size="22" width="3" color="white"/>
                    <template v-else-if="!canGo">
                        {{ $t('common.notEnoughSteps') }} <span class="ml-1">- {{ stepCost }}</span> <v-icon color="white" size="34" class="ml-1">mdi-shoe-print</v-icon>
                    </template>
                    <template v-else>
                        {{ $t('journey.dungeonDetail.enterLabel') }}
                        <span class="ml-1">- {{ stepCost }}</span> <v-icon color="white" size="34" class="ml-1">mdi-shoe-print</v-icon>
                    </template>
                </div>
            </div>
        </div>
    </ResponsiveDialog>
</template>

<script>
    // Detail dungeonu před vstupem - confirm popup s postupem/bossem/možnými
    // překážkami/odměnou, stejný bezpečný princip jako ostatní next/ popupy
    // (žádný SETTYPE navíc, jen existující go-dungeon/start-dungeon-challenge
    // endpointy). Design/historie iterací viz claude/components/journey-dungeon.md.
    import {mapActions, mapGetters} from 'vuex';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import * as journeyApi from '@/api/journeyApi';
    import {pokemonImageSrc} from '@/utils/pokemonImage';

    export default {
        name: 'DungeonDetailDialog',
        components: {ResponsiveDialog},
        props: {
            value: {type: Boolean, default: false},
            dungeon: {type: Object, required: true},
            isChallenge: {type: Boolean, default: false},
            positionId: {type: Number, default: null},
            availableSteps: {type: Number, default: 0},
        },
        data() {
            return {
                obstacleHints: [],
            }
        },
        computed: {
            ...mapGetters({
                isLoading: 'journey/isLoading',
            }),
            bossImg() {
                return this.dungeon.pokemonType ? pokemonImageSrc(this.dungeon.pokemonType.img) : '';
            },
            // stejný obrázek jako miniatura dlaždice v gridu (WildNext.vue)
            dungeonBgStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/wild/thumb/DUNGEON.png')})`};
            },
            bossName() {
                return this.dungeon.pokemonType ? this.dungeon.pokemonType.name : '';
            },
            rewardStoneImg() {
                if (this.isChallenge) {
                    return require('@/assets/pic/stones/ULTIMATE.png');
                }
                const type = this.dungeon.pokemonType && this.dungeon.pokemonType.type;
                return type ? require('@/assets/pic/stones/' + type.id + '.png') : '';
            },
            stepCost() {
                return this.isChallenge ? 7 : 3;
            },
            canGo() {
                return this.availableSteps >= this.stepCost;
            },
        },
        watch: {
            value(isOpen) {
                if (isOpen) {
                    this.loadObstacleHints();
                }
            },
        },
        methods: {
            ...mapActions({
                positionPost: 'journey/positionPost',
                fetchObject: 'journey/fetchObject',
            }),
            loadObstacleHints() {
                if (this.isChallenge) {
                    this.fetchObject(journeyApi.obstacleTypes()).then((data) => {
                        this.obstacleHints = (data && data.member) || [];
                    });
                } else {
                    this.fetchObject(journeyApi.getObstacleHint(this.dungeon.environment)).then((data) => {
                        this.obstacleHints = data || [];
                    });
                }
            },
            go() {
                const request = this.isChallenge
                    ? journeyApi.startDungeonChallenge(this.positionId)
                    : journeyApi.goDungeon(this.positionId);
                this.positionPost(request).then(() => {
                    this.$emit('input', false);
                });
            },
        },
    }
</script>

<style scoped>
.dungeon-dialog-bg {
    flex: 1 1 auto;
    min-height: 0;
    /* pozadi ridi skin (public/skins/normal.css, .skin-window--dungeon) */
    display: flex;
    flex-direction: column;
    /* container query kontext - text i banner rostou/klesají podle skutečné
       šířky popupu (cqw), stejný princip jako .circle-tile-avatar-wrap */
    container-type: inline-size;
}
.dungeon-dialog-banner {
    position: relative;
    flex: none;
    height: clamp(160px, 55cqw, 220px);
    width: 75%;
    /* margin-top nahrazuje odsazení titulku, přesunutého do ResponsiveDialog hlavičky */
    margin: 16px auto 0;
    border-radius: 16px;
    background-color: inherit;
    background-size: cover;
    background-position: center;
    box-shadow: 0 6px 14px rgba(0,0,0,0.3), 0 2px 5px rgba(0,0,0,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
}
.dungeon-dialog-boss-img {
    width: 100%;
    height: 100%;
    filter: drop-shadow(2px 3px 5px rgba(0,0,0,0.6));
}
/* jméno bosse jako popisek POD obrázkem - boss samotný je vidět, jméno
   navíc jako caption v bannerovém rohu místo samostatného textového řádku */
.dungeon-dialog-boss-name {
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: clamp(16px, 5.5cqw, 24px);
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 10px;
    white-space: nowrap;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
}
.dungeon-dialog-reward-stone {
    width: 45px;
    height: 45px;
    flex: none;
}
.dungeon-dialog-mystery {
    font-size: 64px;
    font-weight: 900;
}
.theme--light .dungeon-dialog-mystery {
    color: rgba(0,0,0,0.3);
}
.theme--dark .dungeon-dialog-mystery {
    color: rgba(255,255,255,0.4);
}
.dungeon-dialog-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 20px 20px 86px 20px;
}
.dungeon-dialog-row {
    display: flex;
    align-items: center;
    font-size: clamp(18px, 6.5cqw, 26px);
    font-weight: 700;
}
.theme--light .dungeon-dialog-row {
    color: rgba(0,0,0,0.8);
}
.theme--dark .dungeon-dialog-row {
    color: rgba(255,255,255,0.85);
}
.dungeon-dialog-obstacles {
    text-align: center;
}
.dungeon-dialog-obstacles-label {
    font-size: clamp(16px, 5.5cqw, 22px);
    font-weight: 700;
    margin-bottom: 4px;
}
.theme--light .dungeon-dialog-obstacles-label {
    color: rgba(0,0,0,0.7);
}
.theme--dark .dungeon-dialog-obstacles-label {
    color: rgba(255,255,255,0.8);
}
.dungeon-dialog-obstacles-row {
    display: flex;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
}
.dungeon-dialog-obstacle-icon {
    border-radius: 5px;
    background: rgba(0,0,0,0.15);
    padding: 2px;
}
.dungeon-dialog-action-pill {
    margin-top: 8px;
    background: #6D4C41;
    color: #fff;
    font-size: clamp(20px, 7cqw, 28px);
    font-weight: 700;
    padding: 12px 24px;
    border-radius: 999px;
    text-align: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    min-width: 65%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
        0 6px 14px rgba(109,76,65,0.45),
        1px 1px 4px 3px rgba(0,0,0,0.5),
        -1px -2px 6px 3px rgba(0,0,0,0.65) inset;
}
.dungeon-dialog-action-pill:active {
    box-shadow: 1px 1px 3px 2px rgba(0,0,0,0.7) inset;
}
.dungeon-dialog-action-pill--loading {
    pointer-events: none;
    opacity: 0.85;
}
.dungeon-dialog-action-pill--disabled {
    pointer-events: none;
    background: #BDBDBD;
    box-shadow: 1px 1px 4px 3px rgba(0,0,0,0.25), -1px -2px 6px 3px rgba(0,0,0,0.35) inset;
}
</style>
